/**
 * @fileoverview Registration form component for Privacy Act assessment
 * Collects user details and submits to HubSpot before allowing access to the assessment
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { submitLeadToHubspot, QuestionnaireType } from '@/services/hubspotService';

interface RegistrationFormProps {
  open: boolean;
  onComplete: () => void;
  onClose: () => void;
}

/**
 * Registration form component for collecting user details
 * before allowing access to the Privacy Act assessment
 */
const RegistrationForm: React.FC<RegistrationFormProps> = ({ open, onComplete, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    marketingConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      marketingConsent: checked
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) {
      setError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    if (!formData.marketingConsent) {
      setError('Please accept our terms and conditions according to our privacy policy to continue');
      setIsSubmitting(false);
      return;
    }

    try {
      // Always store data locally first for PDF generation
      sessionStorage.setItem('privacy-act-lead-email', formData.email);
      sessionStorage.setItem('privacy-act-lead-company', formData.company || '');
      
      // TODO: See Sally Wood for the HubSpot list that completed users need to be dropped into
      const success = await submitLeadToHubspot(formData, QuestionnaireType.PRIVACY_ACT);
      
      if (success) {
        onComplete();
      } else {
        setError('Failed to register. Please try again later.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {
      // Prevent closing the dialog - user must complete registration
      return false;
    }}>
      <DialogContent className="sm:max-w-[425px] bg-card" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Register for Privacy Act Assessment</DialogTitle>
          <DialogDescription>
            Enter your details to access the Australian Privacy Act and APPs self-assessment tool.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="marketingConsent"
                checked={formData.marketingConsent}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <Label htmlFor="marketingConsent" className="text-sm font-normal leading-tight">
                <span className="cursor-pointer" onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://deiterate.com/privacy-policy/', '_blank', 'noopener,noreferrer');
                }}>
                  I agree to receive communications from de.iterate. You can unsubscribe at any time. View our Privacy Policy.
                </span>
              </Label>
            </div>
            
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Registering...' : 'Start Assessment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationForm;
